import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { AvailabilityResponse, Slot } from "@/api/contract";
import { addDays, dayLabel, longDate, salonToday, timeLabel } from "@/lib/format";
import { Alert, Spinner } from "./ui";

const WINDOW = 7;

type Fetcher = (p: { serviceId: string; stylistId: string; from: string; days: number; exclude?: string }) => Promise<AvailabilityResponse>;

function period(time: string): "Morning" | "Afternoon" | "Evening" {
  const h = Number(time.slice(0, 2));
  return h < 12 ? "Morning" : h < 17 ? "Afternoon" : "Evening";
}

export function TimePicker({
  fetcher,
  scope,
  serviceId,
  stylistId,
  exclude,
  selected,
  onSelect,
}: {
  fetcher: Fetcher;
  scope: string;
  serviceId: string;
  stylistId: string;
  exclude?: string;
  selected?: Slot;
  onSelect: (slot: Slot) => void;
}) {
  const [from, setFrom] = useState(salonToday());
  const [date, setDate] = useState<string>();

  const q = useQuery({
    queryKey: [scope, "availability", serviceId, stylistId, from, exclude],
    queryFn: () => fetcher({ serviceId, stylistId, from, days: WINDOW, exclude }),
    refetchInterval: 60_000,
  });

  const days = q.data?.days ?? [];
  const firstOpen = days.find((d) => d.slots.length > 0)?.date;

  // Land on the first day with openings whenever a new week loads.
  useEffect(() => {
    if (!q.data) return;
    if (!date || !days.some((d) => d.date === date)) setDate(firstOpen ?? days[0]?.date);
  }, [q.data]);

  const slots = days.find((d) => d.date === date)?.slots ?? [];
  const groups = useMemo(() => {
    const out = new Map<string, Slot[]>();
    for (const s of slots) out.set(period(s.time), [...(out.get(period(s.time)) ?? []), s]);
    return [...out.entries()];
  }, [slots]);

  const canGoBack = from > salonToday();

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="grid size-10 place-items-center rounded-full ring-1 ring-line hover:text-rust disabled:opacity-30"
          onClick={() => setFrom(addDays(from, -WINDOW))}
          disabled={!canGoBack}
          aria-label="Earlier dates"
        >
          <ChevronLeft className="size-5" />
        </button>
        <div className="grid flex-1 grid-cols-7 gap-1.5" role="listbox" aria-label="Choose a date">
          {(days.length ? days : Array.from({ length: WINDOW }, (_, i) => ({ date: addDays(from, i), slots: [] as Slot[] }))).map((d) => {
            const l = dayLabel(d.date);
            const active = d.date === date;
            const empty = q.data && d.slots.length === 0;
            return (
              <button
                key={d.date}
                type="button"
                role="option"
                aria-selected={active}
                aria-label={`${longDate(d.date)}${empty ? ", no openings" : ""}`}
                onClick={() => setDate(d.date)}
                className={clsx(
                  "flex flex-col items-center rounded-2xl py-2.5 transition",
                  active ? "bg-rust text-white shadow-lift" : "bg-white ring-1 ring-line hover:ring-rust/40",
                  empty && !active && "opacity-45",
                )}
              >
                <span className="text-[0.65rem] font-semibold tracking-wide uppercase opacity-80">{l.weekday}</span>
                <span className="font-display text-xl leading-tight">{l.day}</span>
                <span className="text-[0.65rem] opacity-70">{l.month}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="grid size-10 place-items-center rounded-full ring-1 ring-line hover:text-rust"
          onClick={() => setFrom(addDays(from, WINDOW))}
          aria-label="Later dates"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      <div className="mt-6 min-h-48">
        {q.isLoading && <Spinner label="Finding open times" />}
        {q.isError && <Alert tone="error">{(q.error as Error).message}</Alert>}
        {q.data && date && (
          <>
            <p className="font-semibold">{longDate(date)}</p>
            {slots.length === 0 ? (
              <div className="mt-4 rounded-2xl bg-sand px-4 py-6 text-center text-muted">
                No openings this day.{" "}
                {firstOpen ? (
                  <button type="button" className="font-semibold text-rust underline" onClick={() => setDate(firstOpen)}>
                    Next opening: {longDate(firstOpen)}
                  </button>
                ) : (
                  <button type="button" className="font-semibold text-rust underline" onClick={() => setFrom(addDays(from, WINDOW))}>
                    Check next week
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-4 space-y-5">
                {groups.map(([label, list]) => (
                  <div key={label}>
                    <p className="eyebrow mb-2 text-[0.7rem] text-steel">{label}</p>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {list.map((s) => {
                        const active = selected?.start === s.start;
                        return (
                          <button
                            key={s.start}
                            type="button"
                            onClick={() => onSelect(s)}
                            aria-pressed={active}
                            className={clsx(
                              "h-12 rounded-xl text-sm font-semibold transition",
                              active ? "bg-rust text-white shadow-lift" : "bg-white ring-1 ring-line hover:text-rust hover:ring-rust/50",
                            )}
                          >
                            {timeLabel(s.start)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
